import { useEffect, useRef } from "react";

type SignalFieldProps = {
  accent?: string;
  /** Odstęp siatki punktów w px - 36 daje ~1600 punktów na FHD, lekkie dla CPU */
  spacing?: number;
  className?: string;
};

/**
 * Generatywne tło hero - canvas 2D, bez three.js.
 *
 * Idea = copy strony: siatka szarych punktów to SZUM, pojedyncza falująca
 * linia w acid green to SYGNAŁ. Punkty w pobliżu linii rozświetlają się
 * na zielono, punkty przy kursorze delikatnie się rozstępują i jaśnieją.
 *
 * Wydajność:
 *  - punkty rysowane fillRect (nie arc), ~1600 sztuk = spokojne 60 fps,
 *  - pauza gdy karta w tle (visibilitychange) i gdy hero poza viewportem (IO),
 *  - devicePixelRatio ograniczone do 2.
 *
 * Dostępność:
 *  - prefers-reduced-motion: rysuje JEDNĄ statyczną klatkę (siatka + linia),
 *    zero pętli rAF, zero nasłuchu myszy,
 *  - interakcja kursora tylko na pointer: fine.
 *
 * Wpięcie (hero):
 *   <section className="relative">
 *     <SignalField className="absolute inset-0" />
 *     <div className="relative z-10">...istniejąca treść hero...</div>
 *   </section>
 * Zastępuje zdjęcie zespołu - patrz CLAUDE.md, Faza 2, krok 1.
 */
export function SignalField({ accent = "#BEF264", spacing = 36, className }: SignalFieldProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const finePointer = window.matchMedia && window.matchMedia("(pointer: fine)").matches;

    let width = 0;
    let height = 0;
    let dots: { x: number; y: number }[] = [];
    let raf = 0;
    let t = 0;
    let running = false;
    let inViewport = true;
    let mouseX = -9999;
    let mouseY = -9999;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    // accent w RGB do lerpowania koloru punktów
    const hex = accent.replace("#", "");
    const aR = parseInt(hex.slice(0, 2), 16);
    const aG = parseInt(hex.slice(2, 4), 16);
    const aB = parseInt(hex.slice(4, 6), 16);

    function rebuild() {
      const rect = canvas!.parentElement?.getBoundingClientRect();
      width = rect?.width ?? window.innerWidth;
      height = rect?.height ?? window.innerHeight;
      canvas!.width = Math.floor(width * dpr);
      canvas!.height = Math.floor(height * dpr);
      canvas!.style.width = `${width}px`;
      canvas!.style.height = `${height}px`;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);

      dots = [];
      for (let y = spacing / 2; y < height; y += spacing) {
        for (let x = spacing / 2; x < width; x += spacing) {
          dots.push({ x, y });
        }
      }
    }

    function waveY(x: number, time: number) {
      const midY = height * 0.55;
      return (
        midY +
        Math.sin(x * 0.004 + time * 0.9) * height * 0.12 +
        Math.sin(x * 0.011 - time * 1.6) * height * 0.04
      );
    }

    function drawFrame(time: number) {
      ctx!.clearRect(0, 0, width, height);

      // 1. Punkty (szum) - jasność zależna od odległości od sygnału i kursora
      for (const dot of dots) {
        const wy = waveY(dot.x, time);
        const signalGlow = Math.max(0, 1 - Math.abs(dot.y - wy) / 110);

        let dx = 0;
        let dy = 0;
        let cursorGlow = 0;
        if (finePointer) {
          const cx = dot.x - mouseX;
          const cy = dot.y - mouseY;
          const dist = Math.hypot(cx, cy);
          if (dist < 160 && dist > 0.001) {
            const f = 1 - dist / 160;
            dx = (cx / dist) * f * 9;
            dy = (cy / dist) * f * 9;
            cursorGlow = f * 0.5;
          }
        }

        const glow = Math.min(1, signalGlow * 0.85 + cursorGlow);
        const r = Math.round(70 + (aR - 70) * glow);
        const g = Math.round(75 + (aG - 75) * glow);
        const b = Math.round(70 + (aB - 70) * glow);
        const alpha = 0.10 + glow * 0.55;

        ctx!.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;
        const size = 1.5 + glow * 1.5;
        ctx!.fillRect(dot.x + dx - size / 2, dot.y + dy - size / 2, size, size);
      }

      // 2. Linia sygnału - dwa przejścia: szeroka poświata + ostra kreska
      for (const pass of [
        { widthPx: 6, alpha: 0.10 },
        { widthPx: 1.5, alpha: 0.85 },
      ]) {
        ctx!.beginPath();
        for (let x = 0; x <= width; x += 8) {
          const y = waveY(x, time);
          if (x === 0) ctx!.moveTo(x, y);
          else ctx!.lineTo(x, y);
        }
        ctx!.strokeStyle = `rgba(${aR}, ${aG}, ${aB}, ${pass.alpha})`;
        ctx!.lineWidth = pass.widthPx;
        ctx!.stroke();
      }
    }

    function loop() {
      t += 0.016;
      drawFrame(t);
      raf = requestAnimationFrame(loop);
    }

    function start() {
      if (running || !inViewport || document.hidden) return;
      running = true;
      raf = requestAnimationFrame(loop);
    }
    function stop() {
      running = false;
      cancelAnimationFrame(raf);
    }

    function onMouse(e: MouseEvent) {
      const rect = canvas!.getBoundingClientRect();
      mouseX = e.clientX - rect.left;
      mouseY = e.clientY - rect.top;
    }
    function onVisibility() {
      document.hidden ? stop() : start();
    }

    rebuild();

    const ro = new ResizeObserver(() => rebuild());
    ro.observe(canvas.parentElement ?? canvas);

    const io = new IntersectionObserver(
      ([entry]) => {
        inViewport = entry.isIntersecting;
        inViewport ? start() : stop();
      },
      { threshold: 0 },
    );
    io.observe(canvas);

    if (finePointer) window.addEventListener("mousemove", onMouse, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);
    start();

    return () => {
      stop();
      ro.disconnect();
      io.disconnect();
      if (finePointer) window.removeEventListener("mousemove", onMouse);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [accent, spacing]); // reducedMotion no longer used

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={className}
      style={{ display: "block", pointerEvents: "none" }}
    />
  );
}
